
import { of as observableOf,  Observable ,  Subject } from 'rxjs'

import { catchError, switchMap, delay } from 'rxjs/operators'
import {
  mdiBeta,
  mdiCalendarClockOutline,
  mdiChartBoxOutline,
  mdiCogOutline,
  mdiFormatListBulletedSquare,
  mdiHomeOutline,
  mdiTooltipImageOutline
} from '@mdi/js'
import { Icon } from '@mdi/react'
import ExitIcon from '@mui/icons-material/ExitToApp'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { stopImpersonateUser } from 'admin/services/actions'
import { impersonationSelector } from 'admin/services/selectors'
import Logo from 'components/PPLogo'
import { PPNavLink } from 'components/PPNavLink'
import { useEffect, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink } from 'react-router'





import { logout } from 'services/auth'
import { getFailedPostsCountObservable } from 'services/post/actions'
import { failedPostsStatusSelector, selectedProfilesPPIdsSelector } from 'services/post/selectors'
import { currentUserSelector } from 'services/users/selectors'
import { StoreThunkDispatch } from 'store/state'
import styles from './MainNav.pcss'

const FAILED_POSTS_REQUEST_DELAY = 2000 // 2 seconds delay

export function MainNav(props: { className: string }) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const isImpersonating = useSelector(impersonationSelector) !== undefined
  const user = useSelector(currentUserSelector)
  const failedPostsStatus = useSelector(failedPostsStatusSelector)
  const selectedProfilesPpids = useSelector(selectedProfilesPPIdsSelector).join(',')
  const showFailedPostsBadge = failedPostsStatus.hasFailedPosts

  const requestSubject = useRef<Subject<string[]> | null>(null)
  const subscription = useRef<any>(null)

  useEffect(() => {
    requestSubject.current = new Subject<string[]>()

    subscription.current = requestSubject.current.pipe(
      // NOTE: HACK: Adding delay to allow posts fetching requests to run first
      delay(FAILED_POSTS_REQUEST_DELAY), // 2 seconds delay before each call
      switchMap((ppids: string[]) => {
        if (ppids.length > 0) {
          return dispatch(getFailedPostsCountObservable(ppids)).pipe(
            catchError((error: any) => {
              console.error('Failed to get failed posts count:', error)
              return observableOf({ ppids, hasFailedPosts: false })
            }))
        }
        return observableOf({ ppids, hasFailedPosts: false })
      }))
      .subscribe()

    return () => {
      subscription.current?.unsubscribe()
      requestSubject.current?.unsubscribe()
    }
  }, [dispatch])

  useEffect(() => {
    const ppids = selectedProfilesPpids.split(',').filter(id => id.trim() !== '')
    if (requestSubject.current) {
      requestSubject.current.next(ppids)
    }
  }, [selectedProfilesPpids])

  const stopImpersonating = () => {
    dispatch(stopImpersonateUser())
    dispatch(logout())
  }

  return (
    <div className={`${styles['nav-box']} ${props.className}`}>
      <NavLink className={styles['logo-link']} to="/home">
        <Logo className={styles.logo} />
      </NavLink>

      <div className={styles.nav}>
        <PPNavLink to="/home" className={styles.link} activeClassName={styles['link-active']}>
          <div className={styles['round-top']}></div>
          <div className={styles['round-bottom']}></div>
          <div>
            <Icon path={mdiHomeOutline} size="34px" />
            <span className={styles.linkLabel}>Home</span>
          </div>
        </PPNavLink>
        <PPNavLink to="/posts" className={styles.link} activeClassName={styles['link-active']}>
          <div className={styles['round-top']}></div>
          <div className={styles['round-bottom']}></div>
          <div>
            <Icon path={mdiFormatListBulletedSquare} size="34px" />
            <span className={styles.linkLabel}>Posts</span>
          </div>
        </PPNavLink>
        <PPNavLink to="/plans" className={styles.link} activeClassName={styles['link-active']}>
          <div className={styles['round-top']}></div>
          <div className={styles['round-bottom']}></div>
          <div>
            <Icon path={mdiCalendarClockOutline} size="34px" />
            <span className={styles.linkLabel}>Plan</span>
          </div>
        </PPNavLink>

        <PPNavLink to="/history" className={styles.link} activeClassName={styles['link-active']}>
          <div className={styles['round-top']}></div>
          <div className={styles['round-bottom']}></div>
          <div>
            <Icon path={mdiChartBoxOutline} size="34px" />
            <span className={styles.linkLabel}>History</span>
          </div>
          {showFailedPostsBadge && (
            <div className={styles['failed-badge']}></div>
          )}
        </PPNavLink>
        <PPNavLink to="/content" className={styles.link} activeClassName={styles['link-active']}>
          <div className={styles['round-top']}></div>
          <div className={styles['round-bottom']}></div>
          <div>
            <Icon path={mdiTooltipImageOutline} size="34px" />
            <span className={styles.linkLabel}>Content</span>
          </div>
        </PPNavLink>

        {/* <Tooltip
          title={<FormattedMessage id="header.nav.reviews" />}
          placement="right"
          enterDelay={0}
          classes={{ tooltip: styles.tooltip }}
        >
          <PPNavLink to="/reviews" className={styles.link} activeClassName={styles['link-active']}>
            <div className={styles['round-top']}></div>
            <div className={styles['round-bottom']}></div>
            <Icon path={mdiCommentCheckOutline} size="34px" />
          </PPNavLink>
        </Tooltip> */}
      </div>
      <div className={styles['nav-bottom']}>
        {isImpersonating && (
          <Tooltip
            title="Stop impersonating"
            placement="right"
            enterDelay={0}
            classes={{ tooltip: styles.tooltip }}
          >
            <IconButton
              className={`${styles.link} ${styles['link-btn']}`}
              onClick={stopImpersonating}
              size="large"
            >
              <ExitIcon />
            </IconButton>
          </Tooltip>
        )}
        {user?.roles?.isBeta && (
          <Tooltip
            title="You have access to beta features"
            placement="right"
            enterDelay={0}
            classes={{ tooltip: styles.tooltip }}
          >
            <IconButton
              className={`${styles.link} ${styles['link-btn']}`}
              style={{ cursor: 'default' }}
              size="large"
            >
              <Icon path={mdiBeta} size="24px" color="#333" />
            </IconButton>
          </Tooltip>
        )}
        {/* <Tooltip
          title={<FormattedMessage id="header.nav.training" />}
          placement="right"
          enterDelay={0}
          classes={{ tooltip: styles.tooltip }}
        >
          <PPNavLink to="/training" className={styles.link} activeClassName={styles['link-active']}>
            <div className={styles['round-top']}></div>
            <div className={styles['round-bottom']}></div>
            <Icon path={mdiPlayCircle} size="34px" />
          </PPNavLink>
        </Tooltip> */}
        <Tooltip
          title={<FormattedMessage id="header.nav.settings" />}
          placement="right"
          enterDelay={0}
          classes={{ tooltip: styles.tooltip }}
        >
          <PPNavLink to="/settings" className={styles.link} activeClassName={styles['link-active']}>
            <div className={styles['round-top']}></div>
            <div className={styles['round-bottom']}></div>
            <Icon path={mdiCogOutline} size="34px" />
          </PPNavLink>
        </Tooltip>
      </div>
    </div>
  )
}
